// ============================================
// pages/gunny-bags/GunnyTransfersManagement.tsx
// DM / MP / AO_CAO review queue for society-to-society gunny transfers.
// ============================================
import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, CheckCircle, X, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { gunnyTransfersAPI, seasonsAPI } from '../../api/services';
import { UserRole } from '../../types/markfed';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';

interface Transfer {
  id: number;
  source_pacs_id: number;
  dest_pacs_id: number;
  bags: number;
  bag_type: string;
  status: string;
  transfer_date: string | null;
  reason: string | null;
  rejection_reason: string | null;
  sourcePacs?: { id: number; name: string; district_id: number };
  destPacs?: { id: number; name: string; district_id: number };
  source_utilization_id: number | null;
  dest_utilization_id: number | null;
}

export const GunnyTransfersManagement: React.FC = () => {
  const { hasRole } = useAuth();
  const canManage = hasRole(UserRole.DM, UserRole.AO_CAO, UserRole.MANAGER_PROCUREMENT);
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('requested');
  const [rejecting, setRejecting] = useState<{ id: number; reason: string } | null>(null);

  const load = useCallback(async (sid: number) => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await gunnyTransfersAPI.list(sid, filter ? { status: filter } : undefined);
      setTransfers(data);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load transfers');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await seasonsAPI.active();
        if (data?.id) { setSeasonId(data.id); load(data.id); }
      } catch {
        setLoading(false);
      }
    })();
  }, [load]);

  const showFlash = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2500); };

  const approve = async (id: number) => {
    try {
      await gunnyTransfersAPI.review(id, { status: 'approved' });
      showFlash(`Transfer #${id} approved`);
      if (seasonId) load(seasonId);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Approve failed');
    }
  };

  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(transfers, 25);

  const reject = async () => {
    if (!rejecting) return;
    try {
      await gunnyTransfersAPI.review(rejecting.id, { status: 'rejected', rejection_reason: rejecting.reason });
      showFlash(`Transfer #${rejecting.id} rejected`);
      setRejecting(null);
      if (seasonId) load(seasonId);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Reject failed');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Gunny Transfers</h1>
          <p className="text-sm text-gray-600">Review society-to-society transfer requests. Approval moves bags from source consignment and creates a draft consignment at the destination PACS.</p>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-1.5 text-sm border rounded">
          <option value="">All statuses</option>
          <option value="requested">Pending review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {err && <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"><AlertCircle className="w-4 h-4" /> {err}</div>}
      {flash && <div className="mb-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm"><CheckCircle className="w-4 h-4" /> {flash}</div>}

      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : transfers.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No transfers match this filter.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-xs uppercase text-gray-600">
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Source PACS</th>
                <th className="px-3 py-2">→</th>
                <th className="px-3 py-2">Destination PACS</th>
                <th className="px-3 py-2 text-right">Bags</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedItems.map((t) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-3 py-2">{t.id}</td>
                  <td className="px-3 py-2">{t.sourcePacs?.name || `#${t.source_pacs_id}`}</td>
                  <td className="px-3 py-2 text-gray-400">→</td>
                  <td className="px-3 py-2">{t.destPacs?.name || `#${t.dest_pacs_id}`}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.bags.toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs">{t.transfer_date || '—'}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{t.reason || t.rejection_reason || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                      t.status === 'requested' ? 'bg-amber-100 text-amber-700' :
                      t.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                      t.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{t.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canManage && t.status === 'requested' && (
                      <>
                        <button onClick={() => approve(t.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded mr-1">
                          <Check className="w-3 h-3" /> Approve
                        </button>
                        <button onClick={() => setRejecting({ id: t.id, reason: '' })}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-rose-600 hover:bg-rose-700 text-white rounded">
                          <X className="w-3 h-3" /> Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && transfers.length > 0 && (
          <div className="px-3 py-3 border-t bg-gray-50">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              itemLabel="transfers"
            />
          </div>
        )}
      </div>

      {rejecting && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="px-6 py-4 border-b"><h3 className="font-semibold">Reject transfer #{rejecting.id}</h3></div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <textarea rows={3} value={rejecting.reason}
                onChange={(e) => setRejecting({ ...rejecting, reason: e.target.value })}
                className="w-full px-3 py-2 border rounded text-sm" placeholder="e.g. PACS ledger does not show surplus" />
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
              <button onClick={() => setRejecting(null)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">Cancel</button>
              <button onClick={reject} disabled={!rejecting.reason}
                className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GunnyTransfersManagement;
